import React, { useState, useMemo } from 'react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceLine,
} from 'recharts';
import Card from '../../../components/ui/Card';
import { translateTerm as translateTermUtil, fmtBRL, BALANCE_KPIS } from '../utils';

export default function FinancialsTab({ lang, year, petrobrasData, refDate, t, timelineData = {} }) {
  const translateTerm = (term) => translateTermUtil(term, lang);

  const [balanceKpi, setBalanceKpi] = useState(BALANCE_KPIS[0].key);
  const [balanceExpanded, setBalanceExpanded] = useState(false);
  const [debtPieView, setDebtPieView] = useState('type');

  const debtData = useMemo(() => {
    const obrigacoes = petrobrasData['obrigacao'] || [];
    const totalBruto = obrigacoes.reduce((s, o) => s + (parseFloat(o.Divida_Total) || 0), 0);

    // Prefer DFP/ITR values (dfpHistory sorted asc → last write = latest period)
    const dfpRows = timelineData.dfpHistory || [];
    const dfpLatest = {};
    dfpRows.forEach(r => { dfpLatest[r.descricao] = r.value; });

    const pl = dfpLatest['Patrimônio Líquido'] || (() => {
      const finRows = petrobrasData['informacao_financeira'] || [];
      const plRow = finRows
        .filter(r => r.Descricao_Conta === 'Patrimônio Líquido')
        .sort((a, b) => String(b.Data_Fim_Exercicio_Social).localeCompare(String(a.Data_Fim_Exercicio_Social)))[0];
      return parseFloat(plRow?.Valor) || 0;
    })();

    const grossDebt = dfpLatest['Passivo'] || totalBruto;
    const caixa     = dfpLatest['Caixa']   || 0;
    const ebit      = dfpLatest['EBIT']    || 0;
    const da        = dfpLatest['DA']      || 0;
    const netDebt   = grossDebt > 0 ? grossDebt - caixa : 0;
    const ebitda    = ebit > 0 ? ebit + da : 0;
    const latestPeriod = dfpRows.length > 0 ? dfpRows[dfpRows.length - 1].period : refDate;

    return {
      obrigacoes,
      source: null,
      refDate: latestPeriod,
      totalBruto,
      grossDebt,
      netDebt,
      ebitda,
      pdd: 0,
      pl,
    };
  }, [petrobrasData, timelineData, refDate]);

  const leverageDetails = useMemo(() => {
    const d = debtData;
    const safe = (v, fmt) => (v != null && !isNaN(v) && v !== 0) ? fmt(v) : '-';
    const netEbitda = (d.netDebt > 0 && d.ebitda > 0) ? (d.netDebt / d.ebitda).toFixed(2) + 'x' : '-';
    return [
      { label: lang === 'pt' ? 'Dívida Bruta' : 'Gross Debt', val: safe(d.grossDebt, fmtBRL) },
      { label: lang === 'pt' ? 'Dívida Líquida' : 'Net Debt', val: safe(d.netDebt, fmtBRL) },
      { label: 'EBITDA', val: safe(d.ebitda, fmtBRL) },
      { label: lang === 'pt' ? 'Dívida Líq. / EBITDA' : 'Net Debt / EBITDA', val: netEbitda },
      { label: lang === 'pt' ? 'Dívida Bruta / PL' : 'Gross Debt / Equity', val: (d.pl > 0 && d.grossDebt > 0) ? (d.grossDebt / d.pl).toFixed(2) + 'x' : '-' },
      { label: lang === 'pt' ? 'Patrimônio Líquido' : 'Shareholders Equity', val: safe(d.pl, fmtBRL) },
    ];
  }, [debtData, lang]);

  const balanceChartData = useMemo(() => {
    const rows = petrobrasData['informacao_financeira'] || [];

    // ── Dívida Bruta (Endividamento KPI): full history from timeline ──────────
    const timelineDivida = timelineData.dividaBruta || [];
    const endivTimeline = timelineDivida.length > 0
      ? timelineDivida.map(r => ({ period: r.period, passivo: r.dividaBruta }))
      : (() => {
          const total = (petrobrasData['obrigacao'] || [])
            .reduce((s, o) => s + (parseFloat(o.Divida_Total) || 0), 0);
          const fallbackPeriod = (petrobrasData['obrigacao'] || [])[0]?.Data_Fim_Exercicio_Social;
          return fallbackPeriod ? [{ period: fallbackPeriod, passivo: total || null }] : [];
        })();

    // ── Financial KPIs: prefer full timeline history, fall back to current fetch ─
    const historyRows = timelineData.financialHistory || [];
    const dfpRows     = timelineData.dfpHistory       || [];
    // Build lookup: period -> descricao -> value
    const historyByPeriod = {};
    const sourceRows = historyRows.length > 0 ? historyRows : rows.map(r => ({
      period: r.Data_Fim_Exercicio_Social,
      descricao: r.Descricao_Conta,
      value: parseFloat(r.Valor) || null,
    }));
    sourceRows.forEach(r => {
      if (!historyByPeriod[r.period]) historyByPeriod[r.period] = {};
      historyByPeriod[r.period][r.descricao] = r.value;
    });
    // Merge DFP/ITR rows — overrides FRE for same period+descricao, adds 2022+ quarters
    dfpRows.forEach(r => {
      if (!historyByPeriod[r.period]) historyByPeriod[r.period] = {};
      historyByPeriod[r.period][r.descricao] = r.value;
    });

    const finPeriods = Object.keys(historyByPeriod);
    const endivPeriods = endivTimeline.map(r => r.period);
    const periods = [...new Set([...finPeriods, ...endivPeriods])].sort();

    const result = {};
    periods.forEach(period => {
      const endivRow = endivTimeline.find(r => r.period === period);
      const filingYear = parseInt(period.substring(0, 4), 10);
      const entry = { period, filingYear };
      const periodKpis = historyByPeriod[period] || {};
      BALANCE_KPIS.forEach(({ key, source }) => {
        if (source === 'endividamento') {
          // FRE periods: use obrigacao-based dividaBruta; DFP/ITR periods: use aggregated debt
          entry[key] = endivRow ? (endivRow.passivo || null)
                                : (periodKpis['Passivo'] ?? null);
        } else {
          const matchKey = Object.keys(periodKpis).find(k => k.includes(key));
          entry[key] = matchKey != null ? (periodKpis[matchKey] ?? null) : null;
        }
      });
      result[period] = entry;
    });
    return Object.values(result).sort((a, b) => a.period.localeCompare(b.period));
  }, [petrobrasData, timelineData]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

      {/* Balance Sheet */}
      {(() => {
        const allRows = petrobrasData['informacao_financeira'] || [];
        const isFallback = false;
        const balanceRefDate = refDate;
        const periods = [...new Set(allRows.map(r => r.Data_Fim_Exercicio_Social))].sort();
        const latestPeriod = periods[periods.length - 1];
        const allSorted = allRows.slice().sort((a, b) =>
          String(a.Codigo_Conta).localeCompare(String(b.Codigo_Conta)) ||
          String(a.Data_Fim_Exercicio_Social).localeCompare(String(b.Data_Fim_Exercicio_Social))
        );

        return (
          <Card title={t.card_balance_sheet} refDate={balanceRefDate || refDate}>
            {isFallback && (
              <p className="text-[10px] text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2 mb-4">
                {lang === 'pt'
                  ? `Este campo não é reportado no FRE a partir de 2023. Exibindo os dados mais recentes disponíveis.`
                  : `This field is not reported in FRE from 2023 onwards. Showing the most recent available data.`}
              </p>
            )}

            {/* KPI selector */}
            <div className="flex bg-[var(--accent-soft)] p-1 rounded-xl border border-[var(--border)] mb-4 overflow-x-auto gap-0.5">
              {BALANCE_KPIS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setBalanceKpi(key)}
                  className={`flex-1 whitespace-nowrap text-[9px] py-1.5 px-2 rounded-lg font-bold transition-all ${balanceKpi === key ? 'bg-[var(--card)] text-emerald-400 shadow-md border border-[var(--border)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}
                >
                  {label[lang]}
                </button>
              ))}
            </div>

            {/* Trend chart */}
            {(() => {
              const activeKpi = BALANCE_KPIS.find(k => k.key === balanceKpi);
              const chartPoints = balanceChartData.filter(p => p[balanceKpi] !== null);
              const isPerShare = balanceKpi.includes('Ação');
              const fmtVal = v => isPerShare ? `R$ ${Number(v).toFixed(2)}` : fmtBRL(v);
              const latestPoint = chartPoints[chartPoints.length - 1];
              const prevPoint = chartPoints[chartPoints.length - 2];
              const changePct = (latestPoint && prevPoint && prevPoint[balanceKpi])
                ? ((latestPoint[balanceKpi] - prevPoint[balanceKpi]) / Math.abs(prevPoint[balanceKpi])) * 100
                : null;
              return (
                <div className="mb-4">
                  <div className="flex justify-between items-end mb-2">
                    <div>
                      <span className="text-[10px] text-[var(--muted)] uppercase font-semibold tracking-wider block">{activeKpi?.label[lang]}</span>
                      <span className="text-xl font-black text-emerald-500">{latestPoint ? fmtVal(latestPoint[balanceKpi]) : '-'}</span>
                      {latestPoint && <span className="text-[9px] text-[var(--muted)] font-mono ml-2">{latestPoint.period}</span>}
                    </div>
                    {changePct !== null && (
                      <span className={`text-xs font-bold ${changePct >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {changePct >= 0 ? '+' : ''}{changePct.toFixed(1)}% vs prev
                      </span>
                    )}
                  </div>
                  <div className="h-44 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartPoints} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                        <XAxis dataKey="filingYear" tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 10 }} />
                        <YAxis tickLine={false} axisLine={false} tick={{ fill: 'var(--muted)', fontSize: 9 }} tickFormatter={v => isPerShare ? v.toFixed(1) : (Math.abs(v) >= 1e9 ? `${(v/1e9).toFixed(0)}B` : `${(v/1e6).toFixed(0)}M`)} />
                        <Tooltip content={({ active, payload }) => {
                          if (!active || !payload?.length) return null;
                          const p = payload[0].payload;
                          return (
                            <div className="bg-[var(--card)] border border-[var(--border)] p-3 rounded-xl shadow-lg text-xs space-y-1">
                              <p className="text-[var(--muted)] font-mono">{p.period}</p>
                              <p className="font-bold text-emerald-500">{fmtVal(payload[0].value)}</p>
                            </div>
                          );
                        }} />
                        <ReferenceLine y={0} stroke="var(--muted)" strokeDasharray="4 2" strokeOpacity={0.5} />
                        <Line type="monotone" dataKey={balanceKpi} stroke="#10b981" strokeWidth={2.5}
                          dot={{ r: 3, fill: '#10b981', strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: '#34d399', stroke: '#0F1016', strokeWidth: 1.5 }}
                          connectNulls
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              );
            })()}

            <div className="grid grid-cols-2 gap-3 mb-4">
              {BALANCE_KPIS.map(({ key, label }) => {
                const pts = balanceChartData.filter(p => p[key] != null);
                const latest = pts[pts.length - 1];
                const isPS = key.includes('Ação');
                const fmtV = v => isPS ? `R$ ${Number(v).toFixed(2)}` : fmtBRL(v);
                return (
                  <div key={key} className="bg-[var(--accent-soft)]/30 border border-[var(--border)] rounded-xl p-3 space-y-1">
                    <span className="text-[10px] text-[var(--muted)] font-semibold uppercase tracking-wider block truncate">{label[lang]}</span>
                    <span className="text-sm font-black text-emerald-500 block">{latest ? fmtV(latest[key]) : '-'}</span>
                    <span className="text-[9px] text-[var(--muted)] font-mono">{latest?.period || '-'}</span>
                  </div>
                );
              })}
            </div>

            <button
              onClick={() => setBalanceExpanded(v => !v)}
              className="w-full text-[10px] font-semibold text-[var(--muted)] hover:text-[var(--text)] flex items-center justify-center gap-1.5 py-2 border border-[var(--border)] rounded-xl hover:bg-[var(--accent-soft)]/30 transition-all"
            >
              <svg className={`w-3 h-3 transition-transform ${balanceExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              {balanceExpanded
                ? (lang === 'pt' ? 'Ocultar tabela completa' : 'Hide full table')
                : (lang === 'pt' ? `Ver todos os ${allSorted.length} registros (${periods.length} períodos)` : `View all ${allSorted.length} records (${periods.length} periods)`)}
            </button>
            {balanceExpanded && (
              <div className="border border-[var(--border)] rounded-xl overflow-hidden mt-4">
                <table className="w-full text-left border-collapse text-xs">
                  <thead>
                    <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                      <th className="p-3 font-semibold">{t.th_account_code}</th>
                      <th className="p-3 font-semibold">{t.th_account_desc}</th>
                      <th className="p-3 font-semibold text-right">{t.th_value}</th>
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Período' : 'Period'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {allSorted.map((f, idx) => (
                      <tr key={idx} className={`hover:bg-[var(--accent-soft)]/20 ${f.Data_Fim_Exercicio_Social === latestPeriod ? 'bg-emerald-500/[0.03]' : ''}`}>
                        <td className="p-3 font-mono text-[var(--muted)]">{f.Codigo_Conta || '-'}</td>
                        <td className="p-3 font-semibold text-[var(--text)]">{f.Descricao_Conta}</td>
                        <td className="p-3 text-right font-bold text-emerald-500">{fmtBRL(f.Valor)}</td>
                        <td className="p-3 text-[var(--muted)] font-mono text-[10px]">{f.Data_Fim_Exercicio_Social}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })()}

      {/* Debt Leverage + Maturities */}
      <div className="space-y-6">
        <Card title={t.card_debt_leverage} refDate={debtData.refDate || refDate}>
          {debtData.source && (
            <p className="text-[10px] text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2 mb-4">
              {lang === 'pt'
                ? `Este campo não é reportado no FRE a partir de 2023. Exibindo os dados mais recentes disponíveis (${debtData.source}).`
                : `This field is not reported in FRE from 2023 onwards. Showing the most recent available data (${debtData.source}).`}
            </p>
          )}
          <div className="divide-y divide-[var(--border)] text-sm">
            {leverageDetails.map((item, idx) => (
              <div key={idx} className="flex justify-between py-3">
                <span className="text-[var(--muted)]">{item.label}</span>
                <span className="font-bold text-[var(--text)] text-right">{item.val}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card title={t.label_debt_maturities} refDate={debtData.refDate || refDate}>
          {debtData.source && (
            <p className="text-[10px] text-yellow-500 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2 mb-4">
              {lang === 'pt'
                ? `Este campo não é reportado no FRE a partir de 2023. Exibindo os dados mais recentes disponíveis (${debtData.source}).`
                : `This field is not reported in FRE from 2023 onwards. Showing the most recent available data (${debtData.source}).`}
            </p>
          )}
          {(() => {
            const rows = debtData.obrigacoes.filter(o => parseFloat(o.Divida_Total) > 0);
            const byType = {};
            rows.forEach(o => {
              const tipo = translateTerm(o.Tipo_Obrigacao) || 'Outros';
              if (!byType[tipo]) byType[tipo] = 0;
              byType[tipo] += parseFloat(o.Divida_Total) || 0;
            });
            const byMaturity = {
              [t.th_mat_1y]: 0,
              [t.th_mat_1_3y]: 0,
              [t.th_mat_3_5y]: 0,
              [t.th_mat_5y]: 0,
            };
            rows.forEach(o => {
              byMaturity[t.th_mat_1y]   += parseFloat(o.Divida_Inferior_1_Ano) || 0;
              byMaturity[t.th_mat_1_3y] += parseFloat(o.Divida_1a3Anos) || 0;
              byMaturity[t.th_mat_3_5y] += parseFloat(o.Divida_3a5Anos) || 0;
              byMaturity[t.th_mat_5y]   += parseFloat(o.Divida_Superior_5Anos) || 0;
            });
            const byGuarantee = {};
            rows.forEach(o => {
              const guar = o.Tipo_Garantia || 'Outros';
              if (!byGuarantee[guar]) byGuarantee[guar] = 0;
              byGuarantee[guar] += parseFloat(o.Divida_Total) || 0;
            });
            const TYPE_COLORS = ['#10b981','#3b82f6','#f59e0b','#ec4899','#8b5cf6'];
            const MAT_COLORS  = ['#f59e0b','#10b981','#3b82f6','#8b5cf6'];
            const GUAR_COLORS = ['#a78bfa','#fb923c','#34d399','#f472b6','#60a5fa'];
            const typeSlices      = Object.entries(byType).map(([name, value]) => ({ name, value }));
            const maturitySlices  = Object.entries(byMaturity).map(([name, value]) => ({ name, value }));
            const guaranteeSlices = Object.entries(byGuarantee).map(([name, value]) => ({ name, value }));
            const activeSlices = debtPieView === 'type' ? typeSlices : debtPieView === 'maturity' ? maturitySlices : guaranteeSlices;
            const activeColors = debtPieView === 'type' ? TYPE_COLORS : debtPieView === 'maturity' ? MAT_COLORS : GUAR_COLORS;
            const total = activeSlices.reduce((s, d) => s + d.value, 0);
            const DebtTooltip = ({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              const pct = total > 0 ? (d.value / total * 100).toFixed(1) : '0.0';
              return (
                <div className="bg-[var(--card)]/90 backdrop-blur-md border border-[var(--border)] p-3 rounded-xl shadow-xl text-xs space-y-1 z-[100]">
                  <p className="font-bold text-[var(--text)]">{d.name}</p>
                  <p className="text-emerald-400 font-semibold">{fmtBRL(d.value)}</p>
                  <p className="text-yellow-500 font-bold">{pct}%</p>
                </div>
              );
            };
            return (
              <div className="space-y-4">
                <div className="flex bg-[var(--accent-soft)] p-1 rounded-xl border border-[var(--border)]">
                  {[
                    { key: 'type',     label: lang === 'pt' ? 'Por Tipo'      : 'By Type'     },
                    { key: 'maturity', label: lang === 'pt' ? 'Por Vencimento': 'By Maturity'  },
                    { key: 'guarantee', label: lang === 'pt' ? 'Por Garantia'  : 'By Guarantee'  },
                  ].map(({ key, label }) => (
                    <button key={key} onClick={() => setDebtPieView(key)}
                      className={`flex-1 text-[10px] py-1.5 px-2 rounded-lg font-bold transition-all ${debtPieView === key ? 'bg-[var(--card)] text-emerald-400 shadow-md border border-[var(--border)]' : 'text-[var(--muted)] hover:text-[var(--text)]'}`}>
                      {label}
                    </button>
                  ))}
                </div>
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-5 h-[180px] relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip content={<DebtTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                        <Pie data={activeSlices} cx="50%" cy="50%" innerRadius={45} outerRadius={65} paddingAngle={2} dataKey="value">
                          {activeSlices.map((_, i) => <Cell key={i} fill={activeColors[i % activeColors.length]} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                      <span className="text-[9px] font-bold text-[var(--muted)] uppercase tracking-wider">Total</span>
                      <span className="text-[10px] font-black text-[var(--text)]">{fmtBRL(total)}</span>
                    </div>
                  </div>
                  <div className="col-span-7 space-y-1.5 max-h-[180px] overflow-y-auto pr-1">
                    {activeSlices.map((entry, i) => {
                      const pct = total > 0 ? (entry.value / total * 100).toFixed(1) : '0.0';
                      return (
                        <div key={i} className="flex items-center justify-between text-[10px] p-1.5 rounded-lg hover:bg-[var(--accent-soft)]/20">
                          <div className="flex items-center gap-2 truncate pr-2">
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: activeColors[i % activeColors.length] }} />
                            <span className="text-[var(--text)] font-semibold truncate">{entry.name}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <span className="text-[var(--muted)] text-[8px] font-mono">{fmtBRL(entry.value)}</span>
                            <span className="text-yellow-500 font-bold font-mono w-[38px] text-right">{pct}%</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })()}
        </Card>
      </div>

      {/* Outros Valores Mobiliários */}
      {(() => {
        const ovmRows = petrobrasData['outro_valor_mobiliario'] || [];
        const totalSaldoOvm = ovmRows.reduce((s, r) => s + (parseFloat(r.Saldo_Devedor) || 0), 0);
        return (
          <Card
            title={lang === 'pt' ? 'Outros Valores Mobiliários' : 'Other Securities'}
            subtitle={ovmRows.length
              ? (lang === 'pt' ? `${ovmRows.length} emissões · Saldo: ${fmtBRL(totalSaldoOvm)}` : `${ovmRows.length} issuances · Balance: ${fmtBRL(totalSaldoOvm)}`)
              : undefined}
            refDate={refDate}
          >
            {ovmRows.length > 0 ? (
              <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
                <table className="w-full text-left border-collapse text-xs min-w-[500px]">
                  <thead>
                    <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Tipo' : 'Type'}</th>
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Identificação' : 'Description'}</th>
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Emissão' : 'Issue'}</th>
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Vencimento' : 'Maturity'}</th>
                      <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Saldo Devedor' : 'Outstanding'}</th>
                      <th className="p-3 font-semibold">{lang === 'pt' ? 'Conversível' : 'Convertible'}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {ovmRows.map((r, idx) => (
                      <tr key={idx} className="hover:bg-[var(--accent-soft)]/20">
                        <td className="p-3">
                          <span className="bg-yellow-500/10 border border-yellow-500/20 text-yellow-500 text-[9px] px-2 py-0.5 rounded font-bold uppercase tracking-wider">
                            {r.Valor_Mobiliario || '-'}
                          </span>
                        </td>
                        <td className="p-3 text-[var(--text)] max-w-[220px] truncate font-semibold" title={r.Identificacao_Valor_Mobiliario}>{r.Identificacao_Valor_Mobiliario || '-'}</td>
                        <td className="p-3 font-mono text-[var(--muted)] text-[10px]">{r.Data_Emissao || '-'}</td>
                        <td className="p-3 font-mono text-[var(--muted)] text-[10px]">{r.Data_Vencimento || '-'}</td>
                        <td className="p-3 text-right font-bold text-emerald-500">{fmtBRL(r.Saldo_Devedor)}</td>
                        <td className="p-3 text-[var(--muted)]">{r.Conversibilidade || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-10 text-center space-y-2">
                <span className="text-2xl">—</span>
                <p className="text-xs text-[var(--muted)]">
                  {lang === 'pt' ? 'Nenhuma emissão registrada no FRE.' : 'No issuances registered in FRE.'}
                </p>
              </div>
            )}
          </Card>
        );
      })()}

      {/* Títulos no Exterior */}
      {(() => {
        const teRows = (petrobrasData['titulo_exterior'] || []).slice().sort((a, b) => String(a.Data_Vencimento).localeCompare(String(b.Data_Vencimento)));
        if (!teRows.length) return null;
        const totalSaldoTe = teRows.reduce((s, r) => s + (parseFloat(r.Saldo_Devedor) || 0), 0);
        return (
          <Card
            title={lang === 'pt' ? 'Títulos no Exterior (Global Notes / Bonds)' : 'Foreign Securities (Global Notes / Bonds)'}
            subtitle={lang === 'pt' ? `${teRows.length} emissões · Saldo total: ${fmtBRL(totalSaldoTe)}` : `${teRows.length} issuances · Total outstanding: ${fmtBRL(totalSaldoTe)}`}
            refDate={refDate}
          >
            <div className="border border-[var(--border)] rounded-xl overflow-x-auto">
              <table className="w-full text-left border-collapse text-xs min-w-[700px]">
                <thead>
                  <tr className="bg-[var(--accent-soft)] border-b border-[var(--border)] text-[var(--muted)]">
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Título' : 'Security'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Emissão' : 'Issue'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Vencimento' : 'Maturity'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Valor Nominal' : 'Nominal Value'}</th>
                    <th className="p-3 font-semibold text-right">{lang === 'pt' ? 'Saldo Devedor' : 'Outstanding'}</th>
                    <th className="p-3 font-semibold">{lang === 'pt' ? 'Resgatável' : 'Redeemable'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--border)]">
                  {teRows.map((r, idx) => {
                    const isExpired = r.Data_Vencimento && r.Data_Vencimento < String(year);
                    return (
                      <tr key={idx} className={`hover:bg-[var(--accent-soft)]/20 ${isExpired ? 'opacity-50' : ''}`}>
                        <td className="p-3 font-bold text-[var(--text)] font-mono text-[10px]">{r.Identificacao_Valor_Mobiliario || '-'}</td>
                        <td className="p-3 font-mono text-[var(--muted)] text-[10px]">{r.Data_Emissao || '-'}</td>
                        <td className="p-3 font-mono text-[10px]">
                          <span className={r.Data_Vencimento > String(year + 10) ? 'text-blue-400 font-bold' : 'text-[var(--muted)]'}>{r.Data_Vencimento || '-'}</span>
                        </td>
                        <td className="p-3 text-right text-[var(--muted)]">{fmtBRL(r.Valor_Nominal)}</td>
                        <td className="p-3 text-right font-bold text-emerald-500">{fmtBRL(r.Saldo_Devedor)}</td>
                        <td className="p-3 text-[var(--muted)]">
                          {r.Possibilidade_Resgate === 'S'
                            ? <span className="text-yellow-500 font-semibold">{lang === 'pt' ? 'Sim' : 'Yes'}</span>
                            : <span className="text-[var(--muted)]">{lang === 'pt' ? 'Não' : 'No'}</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        );
      })()}

    </div>
  );
}
